// Pure state-transition logic for the lesson-builder page's local editing
// state. No I/O, no React -- see docs/BLUEPRINT-2026-09-29-lesson-builder-reducer-extraction.md
// for why this is split from lesson-builder.tsx and what its boundary is.

import { extractVideoLinksByPage, VideoLink } from 'lesson-builder/videoLinks'
import { Question, QuestionsByDifficulty, QuestionSlide as QuestionSlideType, Lesson, FillBlankQuestion, MultipleChoiceQuestion, ShortAnswerQuestion } from '@all-for-one/shared-types'

export interface Slide {
  type: 'presentation' | 'question'
  index: number
  id?: string
  learningStage: string
  questionsByDifficulty: QuestionsByDifficulty[]
  allApproved: boolean
  slide?: number  // Original slide number from questionSlides
}

export interface LessonState {
  name: string
  lessonPdf: string | null
  videoLinksByPage: Record<number, VideoLink[]>
  numSlides: number
  questionSlides: QuestionSlideType[]  // SOURCE OF TRUTH - never deep copy
  totalUnapprovedQuestions: number
  currentSlide: number
  hideSidebar: boolean
  status: string
  lastEditTimestamp: number | null  // Track when "Done" was clicked on an edit
}

export type LessonAction =
  | { type: 'INIT_LESSON'; payload: Lesson }
  | { type: 'APPROVE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }
  | { type: 'DISAPPROVE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }
  | { type: 'APPROVE_ALL_QUESTIONS'; slideIndex: number }
  | { type: 'EDIT_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string; payload: Partial<Question> }
  | { type: 'DELETE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }
  | { type: 'SET_CURRENT_SLIDE'; slideNum: number }
  | { type: 'TOGGLE_SIDEBAR' }

// Utility functions for question handling
const isAssessmentStage = (stage: string): boolean =>
  stage === 'Pre-Assessment' || stage === 'Post-Assessment'

// Type-safe merge function that preserves discriminated union types
const mergeQuestion = (question: Question, payload: Partial<Question>): Question => {
  // Never let an incoming payload overwrite the question's id. Pre/Post twins
  // share a base id but must keep their distinct _Pre-Assessment / _Post-Assessment
  // suffixes for separate tracking. A refine/edit payload carries the edited
  // question's own id, which — when the edit is synced to the matching twin —
  // would otherwise clobber the twin's suffix. Ids are immutable after generation,
  // so pinning to the target's own id is always correct.
  const safePayload: Partial<Question> = { ...payload, id: question.id }

  // If payload has a type and it's different from the current type, we need to handle type conversion
  if (safePayload.type && safePayload.type !== question.type) {
    // This is a type conversion - we'll need to handle this case specifically
    return Object.assign({}, question, safePayload) as Question
  }

  // For same-type updates, we can safely merge
  switch (question.type) {
    case 'fill-blank':
      return Object.assign({}, question, safePayload) as FillBlankQuestion
    case 'multiple-choice':
      return Object.assign({}, question, safePayload) as MultipleChoiceQuestion
    case 'short-answer':
      return Object.assign({}, question, safePayload) as ShortAnswerQuestion
  }
}

// Helper function to extract base ID (remove section suffix for comparison)
const getBaseQuestionId = (id: string): string => {
  return id.replace(/_(Pre-Assessment|Post-Assessment|Check for Understanding|Independent Work)$/, '')
}

// Build slides array from questionSlides for rendering
export const buildSlidesArray = (questionSlides: QuestionSlideType[], numSlides: number): Slide[] => {
  return Array(numSlides).fill(null).flatMap((_, i) => [
    { type: 'presentation' as const, index: i, learningStage: '', questionsByDifficulty: [], allApproved: true },
    ...questionSlides
      .filter(q => q.slide === i)
      .map(q => ({
        type: 'question' as const,
        index: i,
        id: crypto.randomUUID(),  // Just for React keys
        learningStage: q.learningStage,
        questionsByDifficulty: q.questionsByDifficulty,
        allApproved: q.allApproved || false,
        slide: q.slide  // Keep reference to original slide number
      }))
  ])
}

// Calculate approved status for a slide
const calculateSlideApproved = (questionsByDifficulty: QuestionsByDifficulty[]): boolean => {
  return questionsByDifficulty.every(
    diffQuestions => diffQuestions.questions.every(q => q.approved)
  )
}

// Calculate total unapproved questions from questionSlides
const calculateLessonUnapprovedFromQuestionSlides = (questionSlides: QuestionSlideType[]): number => {
  // Get unique assessment questions (only count once - take first assessment slide)
  const firstAssessmentSlide = questionSlides.find(qSlide => isAssessmentStage(qSlide.learningStage))
  const assessmentQuestions = firstAssessmentSlide?.questionsByDifficulty || []

  // Get non-assessment questions
  const nonAssessmentQuestions = questionSlides
    .filter(qSlide => !isAssessmentStage(qSlide.learningStage))
    .flatMap(qSlide => qSlide.questionsByDifficulty)

  return [...assessmentQuestions, ...nonAssessmentQuestions]
    .flatMap(difficulty => difficulty.questions)
    .filter(q => !q.approved)
    .length
}

/**
 * Updates a specific question in array of QuestionsByDifficulty objects
 * Used by approval and edit handlers to modify question state.
 *
 * @param questions - Array of questions by difficulty
 * @param difficulty - Target difficulty level to update
 * @param questionIndex - Index of question to update
 * @param updateFn - Function to apply to the target question
 * @returns New questions array with updated question
 */
const updateQuestionsByDifficulty = (
  questionsByDifficulty: QuestionsByDifficulty[],
  difficulty: string,
  questionIndex: number,
  updateFn: (q: Question) => Question
): QuestionsByDifficulty[] => {
  return questionsByDifficulty.map((diffSection: QuestionsByDifficulty) => {
    if (diffSection.difficulty !== difficulty) return diffSection

    return {
      ...diffSection,
      questions: diffSection.questions.map((q: Question, qIndex: number) => {
        if (qIndex !== questionIndex) return q
        return updateFn(q) // Apply update function to target question
      })
    }
  })
}

// Display label helpers (mirrors backend display-labels.ts)
const SECTION_ABBR: Record<string, string> = {
  'Pre-Assessment': 'PRE', 'Post-Assessment': 'POS',
  'Check for Understanding': 'CFU', 'Check-for-Understanding': 'CFU',
  'Independent Work': 'IND', 'Independent-Work': 'IND',
}
const LEVEL_LETTER: Record<string, string> = { 'Emerging': 'E', 'Intermediate': 'I', 'Advanced': 'A' }

const recalculateDisplayLabels = (questionSlides: QuestionSlideType[]): QuestionSlideType[] => {
  return questionSlides.map(slide => ({
    ...slide,
    questionsByDifficulty: slide.questionsByDifficulty.map(diffGroup => ({
      ...diffGroup,
      questions: diffGroup.questions.map((q, i) => ({
        ...q,
        displayLabel: `${slide.slide + 1}.${i + 1}.${SECTION_ABBR[slide.learningStage] ?? slide.learningStage}.${LEVEL_LETTER[diffGroup.difficulty] ?? diffGroup.difficulty}`
      }))
    }))
  }))
}

// Action handlers
const handleInitLesson = (state: LessonState, action: { type: 'INIT_LESSON'; payload: any }): LessonState => {
  // Simply store questionSlides as-is (NO deep copy, NO transformation)
  const questionSlides = action.payload.questionSlides || []

  return {
    ...state,
    name: action.payload.name,
    lessonPdf: action.payload.lessonPdf,
    videoLinksByPage: extractVideoLinksByPage(action.payload.eduquerySlides),
    numSlides: action.payload.numSlides,
    questionSlides: questionSlides,
    status: action.payload.status,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(questionSlides)
    // lastEditTimestamp preserved from ...state spread (don't reset on data refetch)
  }
}

const handleApproveQuestion = (state: LessonState, action: { type: 'APPROVE_QUESTION' | 'DISAPPROVE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }): LessonState => {
  // Build slides array to find which questionSlide corresponds to slideIndex
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]

  if (!targetSlide || targetSlide.type !== 'question') return state

  const approved = action.type === 'APPROVE_QUESTION'
  const isDisapproved = action.type === 'DISAPPROVE_QUESTION'
  const newStatus = isDisapproved && state.status === 'Ready' ? 'Draft' : state.status

  // For assessment slides: update all Pre/Post by matching base ID
  if (isAssessmentStage(targetSlide.learningStage)) {
    // Find target question's base ID
    const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
    const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
    if (!targetQuestion) return state

    const baseId = getBaseQuestionId(targetQuestion.id)

    // Update all assessment questionSlides with matching base ID
    const newQuestionSlides = state.questionSlides.map(qSlide => {
      if (!isAssessmentStage(qSlide.learningStage)) return qSlide

      return {
        ...qSlide,
        questionsByDifficulty: qSlide.questionsByDifficulty.map(diffGroup => {
          if (diffGroup.difficulty !== action.difficulty) return diffGroup

          return {
            ...diffGroup,
            questions: diffGroup.questions.map(q => {
              if (getBaseQuestionId(q.id) === baseId) {
                return { ...q, approved, isDisapproved }
              }
              return q
            })
          }
        }),
        allApproved: calculateSlideApproved(
          qSlide.questionsByDifficulty.map(diffGroup => {
            if (diffGroup.difficulty !== action.difficulty) return diffGroup
            return {
              ...diffGroup,
              questions: diffGroup.questions.map(q => {
                if (getBaseQuestionId(q.id) === baseId) {
                  return { ...q, approved, isDisapproved }
                }
                return q
              })
            }
          })
        )
      }
    })

    return {
      ...state,
      questionSlides: newQuestionSlides,
      totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
      status: newStatus,
      lastEditTimestamp: Date.now()  // Mark that an approval happened
    }
  }

  // For non-assessment: only update the matching questionSlide
  const newQuestionSlides = state.questionSlides.map(qSlide => {
    if (qSlide.learningStage !== targetSlide.learningStage || qSlide.slide !== targetSlide.slide) {
      return qSlide
    }

    const newQuestionsByDifficulty = updateQuestionsByDifficulty(
      qSlide.questionsByDifficulty,
      action.difficulty,
      action.questionIndex,
      q => ({ ...q, approved, isDisapproved })
    )

    return {
      ...qSlide,
      questionsByDifficulty: newQuestionsByDifficulty,
      allApproved: calculateSlideApproved(newQuestionsByDifficulty)
    }
  })

  return {
    ...state,
    questionSlides: newQuestionSlides,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
    status: newStatus,
    lastEditTimestamp: Date.now()  // Mark that an edit just happened
  }
}

const handleApproveAllQuestions = (state: LessonState, action: { type: 'APPROVE_ALL_QUESTIONS'; slideIndex: number }): LessonState => {
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]

  if (!targetSlide || targetSlide.type !== 'question') return state

  const approveAllInSlide = (qSlide: QuestionSlideType): QuestionSlideType => ({
    ...qSlide,
    questionsByDifficulty: qSlide.questionsByDifficulty.map(diffGroup => ({
      ...diffGroup,
      questions: diffGroup.questions.map(q => ({ ...q, approved: true, isDisapproved: false }))
    })),
    allApproved: true
  })

  let newQuestionSlides: QuestionSlideType[]
  if (isAssessmentStage(targetSlide.learningStage)) {
    newQuestionSlides = state.questionSlides.map(qSlide =>
      isAssessmentStage(qSlide.learningStage) ? approveAllInSlide(qSlide) : qSlide
    )
  } else {
    newQuestionSlides = state.questionSlides.map(qSlide =>
      qSlide.learningStage === targetSlide.learningStage && qSlide.slide === targetSlide.slide
        ? approveAllInSlide(qSlide)
        : qSlide
    )
  }

  return {
    ...state,
    questionSlides: newQuestionSlides,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
    lastEditTimestamp: Date.now()
  }
}

const handleEditQuestion = (state: LessonState, action: { type: 'EDIT_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string; payload: Partial<Question> }): LessonState => {
  // Build slides array to find which questionSlide corresponds to slideIndex
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]

  if (!targetSlide || targetSlide.type !== 'question') return state

  // For assessment slides: update all Pre/Post by matching base ID
  if (isAssessmentStage(targetSlide.learningStage)) {
    // Find target question's base ID
    const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
    const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
    if (!targetQuestion) return state

    const baseId = getBaseQuestionId(targetQuestion.id)

    // Update all assessment questionSlides with matching base ID
    const newQuestionSlides = state.questionSlides.map(qSlide => {
      if (!isAssessmentStage(qSlide.learningStage)) return qSlide

      return {
        ...qSlide,
        questionsByDifficulty: qSlide.questionsByDifficulty.map(diffGroup => {
          if (diffGroup.difficulty !== action.difficulty) return diffGroup

          return {
            ...diffGroup,
            questions: diffGroup.questions.map(q => {
              if (getBaseQuestionId(q.id) === baseId) {
                const merged = mergeQuestion(q, action.payload)
                return { ...merged, approved: false, isDisapproved: false }
              }
              return q
            })
          }
        }),
        allApproved: false  // Editing unapproves the slide
      }
    })

    return {
      ...state,
      questionSlides: newQuestionSlides,
      totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
      lastEditTimestamp: Date.now()
    }
  }

  // For non-assessment: only update the matching questionSlide
  const newQuestionSlides = state.questionSlides.map(qSlide => {
    if (qSlide.learningStage !== targetSlide.learningStage || qSlide.slide !== targetSlide.slide) {
      return qSlide
    }

    const newQuestionsByDifficulty = updateQuestionsByDifficulty(
      qSlide.questionsByDifficulty,
      action.difficulty,
      action.questionIndex,
      q => {
        const merged = mergeQuestion(q, action.payload)
        return { ...merged, approved: false, isDisapproved: false }
      }
    )

    return {
      ...qSlide,
      questionsByDifficulty: newQuestionsByDifficulty,
      allApproved: false  // Editing unapproves the slide
    }
  })

  return {
    ...state,
    questionSlides: newQuestionSlides,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(newQuestionSlides),
    lastEditTimestamp: Date.now()  // Mark that an edit just happened
  }
}

const handleDeleteQuestion = (state: LessonState, action: { type: 'DELETE_QUESTION'; slideIndex: number; questionIndex: number; difficulty: string }): LessonState => {
  const slides = buildSlidesArray(state.questionSlides, state.numSlides)
  const targetSlide = slides[action.slideIndex]
  if (!targetSlide || targetSlide.type !== 'question') return state

  if (isAssessmentStage(targetSlide.learningStage)) {
    // Find the question's base ID to remove from all assessment slides
    const targetDiffGroup = targetSlide.questionsByDifficulty.find(d => d.difficulty === action.difficulty)
    const targetQuestion = targetDiffGroup?.questions[action.questionIndex]
    if (!targetQuestion) return state
    const baseId = getBaseQuestionId(targetQuestion.id)

    const newQuestionSlides = state.questionSlides.map(qSlide => {
      if (!isAssessmentStage(qSlide.learningStage)) return qSlide
      const newQbd = qSlide.questionsByDifficulty.map(diffGroup => {
        if (diffGroup.difficulty !== action.difficulty) return diffGroup
        return {
          ...diffGroup,
          questions: diffGroup.questions.filter(q => getBaseQuestionId(q.id) !== baseId)
        }
      })
      return {
        ...qSlide,
        questionsByDifficulty: newQbd,
        allApproved: calculateSlideApproved(newQbd)
      }
    })

    const relabeled = recalculateDisplayLabels(newQuestionSlides)
    return {
      ...state,
      questionSlides: relabeled,
      totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(relabeled),
      lastEditTimestamp: Date.now()
    }
  }

  // Non-assessment: remove from the matching questionSlide
  const newQuestionSlides = state.questionSlides.map(qSlide => {
    if (qSlide.learningStage !== targetSlide.learningStage || qSlide.slide !== targetSlide.slide) return qSlide
    const newQbd = qSlide.questionsByDifficulty.map(diffGroup => {
      if (diffGroup.difficulty !== action.difficulty) return diffGroup
      return {
        ...diffGroup,
        questions: diffGroup.questions.filter((_, idx) => idx !== action.questionIndex)
      }
    })
    return {
      ...qSlide,
      questionsByDifficulty: newQbd,
      allApproved: calculateSlideApproved(newQbd)
    }
  })

  const relabeled = recalculateDisplayLabels(newQuestionSlides)
  return {
    ...state,
    questionSlides: relabeled,
    totalUnapprovedQuestions: calculateLessonUnapprovedFromQuestionSlides(relabeled),
    lastEditTimestamp: Date.now()
  }
}

// Main reducer
export const lessonReducer = (state: LessonState, action: LessonAction): LessonState => {
  switch (action.type) {
    case 'INIT_LESSON':
      return handleInitLesson(state, action)

    case 'APPROVE_QUESTION':
    case 'DISAPPROVE_QUESTION':
      return handleApproveQuestion(state, action)

    case 'APPROVE_ALL_QUESTIONS':
      return handleApproveAllQuestions(state, action)

    case 'EDIT_QUESTION':
      return handleEditQuestion(state, action)

    case 'DELETE_QUESTION':
      return handleDeleteQuestion(state, action)

    case 'SET_CURRENT_SLIDE':
      return {
        ...state,
        currentSlide: action.slideNum
      }

    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        hideSidebar: !state.hideSidebar
      }

    default:
      return state
  }
}
